import { useState, useRef, useEffect, useCallback } from 'react';

export function useDragToScroll(dependencies = []) {
  const scrollContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const isSyncingScroll = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth: sw, clientWidth } = scrollContainerRef.current;
        setCanScroll(sw > clientWidth);
        setScrollWidth(sw);
      }
    };
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, dependencies);

  const handleTopScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    if (scrollContainerRef.current && topScrollRef.current) {
      scrollContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    setTimeout(() => { isSyncingScroll.current = false; }, 10);
  }, []);

  const handleBottomScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    if (topScrollRef.current && scrollContainerRef.current) {
      topScrollRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
    }
    setTimeout(() => { isSyncingScroll.current = false; }, 10);
  }, []);

  const isInteractiveElement = useCallback((element) => {
    const interactiveTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
    let current = element;
    while (current && current !== scrollContainerRef.current) {
      if (
        interactiveTags.includes(current.tagName) ||
        current.getAttribute('role') === 'button' ||
        current.classList.contains('cursor-pointer') ||
        current.onclick
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (isInteractiveElement(e.target)) return;

      setIsDragging(true);
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
      setScrollLeft(scrollContainerRef.current.scrollLeft);
    },
    [isInteractiveElement]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x = e.pageX - scrollContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return {
    scrollContainerRef,
    topScrollRef,
    isDragging,
    canScroll,
    scrollWidth,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      onScroll: handleBottomScroll,
    },
    topScrollHandler: handleTopScroll,
  };
}

export default useDragToScroll;
